import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import moment from 'moment';
import { QRCodeSVG } from 'qrcode.react';
import { AppContext } from '../../context/AppContext';
import InlineMessage, { InlineMessageState } from '../InlineMessage';
import PasswordConfirm from '../PasswordConfirm';
import RecoveryCodesDisplay from '../RecoveryCodesDisplay';
import { TotpSetup, TwoFactorStatus } from '../../services/TwoFactorService';
import { createCredential, isWebAuthnSupported } from '../../utils/webauthn';

/**
 * which destructive confirmation is currently unfolded. only ever one at a
 * time, so a stray second password field cannot exist
 */
type OpenConfirm =
  | { kind: 'none' }
  | { kind: 'disable-totp' }
  | { kind: 'delete-passkey'; id: string }
  | { kind: 'regenerate-codes' }
  | { kind: 'forget-devices' };

/**
 * detectDeviceName
 *
 * a passkey is worth nothing to the user if every entry in the list is called
 * "Passkey". the user agent is a guess and nothing more - the field stays
 * editable, this only spares the common case the typing.
 *
 * the order of the checks matters: Edge and Opera also carry "Chrome" in their
 * user agent, and every iPad since iPadOS 13 claims to be a Macintosh, which is
 * why the touch point count decides that one.
 */
function detectDeviceName(): { browser: string; system: string } {
  const agent = (typeof navigator !== 'undefined' && navigator.userAgent) || '';

  let browser = '',
    system = '';

  if (agent.indexOf('Edg/') !== -1) {
    browser = 'Edge';
  } else if (agent.indexOf('OPR/') !== -1 || agent.indexOf('Opera') !== -1) {
    browser = 'Opera';
  } else if (agent.indexOf('Firefox/') !== -1) {
    browser = 'Firefox';
  } else if (agent.indexOf('Chrome/') !== -1) {
    browser = 'Chrome';
  } else if (agent.indexOf('Safari/') !== -1) {
    browser = 'Safari';
  }

  if (agent.indexOf('Android') !== -1) {
    system = 'Android';
  } else if (/iPhone|iPod/.test(agent)) {
    system = 'iOS';
  } else if (agent.indexOf('iPad') !== -1) {
    system = 'iPadOS';
  } else if (agent.indexOf('Mac OS X') !== -1) {
    /**
     * an iPad in desktop mode is indistinguishable from a mac by the user agent
     * alone; only a touch screen tells them apart
     */
    system = (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1) ? 'iPadOS' : 'macOS';
  } else if (agent.indexOf('CrOS') !== -1) {
    system = 'ChromeOS';
  } else if (agent.indexOf('Windows') !== -1) {
    system = 'Windows';
  } else if (agent.indexOf('Linux') !== -1) {
    system = 'Linux';
  }

  return { browser: browser, system: system };
}

/**
 * TwoFactorSettings
 *
 * the self service half of INTERLINK-9: authenticator app, passkeys, recovery
 * codes and trusted devices, all fed by the single GET /user/two-factor.
 *
 * every destructive action unfolds a confirmation right where it was triggered
 * (see PasswordConfirm) because the project has no modal system, and every
 * result is reported through InlineMessage because it has no toast system
 * either.
 */
const TwoFactorSettings: React.FC = () => {
  const {
    services: {
      serviceTwoFactor
    },
    I18N
  } = useContext(AppContext);

  const [ status, setStatus ] = useState<TwoFactorStatus | null>(null);
  const [ refreshing, setRefreshing ] = useState(true);
  const [ loadFailed, setLoadFailed ] = useState(false);
  const [ reloadToken, setReloadToken ] = useState(0);

  const [ totpSetup, setTotpSetup ] = useState<TotpSetup | null>(null);
  const [ totpCode, setTotpCode ] = useState('');
  const [ totpMessage, setTotpMessage ] = useState<InlineMessageState | null>(null);

  const [ passkeyName, setPasskeyName ] = useState('');
  const [ passkeyMessage, setPasskeyMessage ] = useState<InlineMessageState | null>(null);
  const [ renamingId, setRenamingId ] = useState<string | null>(null);
  const [ renameValue, setRenameValue ] = useState('');

  const [ recoveryMessage, setRecoveryMessage ] = useState<InlineMessageState | null>(null);
  const [ deviceMessage, setDeviceMessage ] = useState<InlineMessageState | null>(null);

  /**
   * the clear text codes plus where they came from - the block looks the same
   * in both cases but the surrounding section differs
   */
  const [ freshCodes, setFreshCodes ] = useState<{ codes: string[]; source: string } | null>(null);

  const [ confirm, setConfirm ] = useState<OpenConfirm>({ kind: 'none' });
  const [ busy, setBusy ] = useState<string>('');

  const webAuthnAvailable = isWebAuthnSupported();

  /**
   * load the status. the guard is not cosmetic: the panel can be left through
   * the tab bar while the request is still open, and a setState afterwards would
   * be a leak warning at best and a resurrected error message at worst
   */
  useEffect(() => {
    let active = true;

    setRefreshing(true);

    serviceTwoFactor.getStatus()
      .then((data) => {
        if (!active) {
          return;
        }

        setStatus(data);
        setLoadFailed(false);
      })
      .catch(() => {
        if (!active) {
          return;
        }

        setLoadFailed(true);
      })
      .then(() => {
        if (active) {
          setRefreshing(false);
        }
      });

    return () => {
      active = false;
    };
  }, [serviceTwoFactor, reloadToken]);

  /**
   * reload
   */
  const reload = () => setReloadToken((token) => token + 1);

  /**
   * describeError
   * the http status is the only thing worth telling the user apart; the server
   * message is english and untranslated, so it never reaches the screen
   * @param error
   * @param fallback already translated
   */
  const describeError = (error: any, fallback: string): string => {
    const response = error && error.response,
      code = response && response.status;

    if (code === 401 || code === 403) {
      return I18N.t("Das aktuelle Passwort ist falsch.");
    }

    if (code === 422) {
      return I18N.t("Die Eingabe wurde nicht akzeptiert. Bitte prüfen Sie sie.");
    }

    if (code === 429) {
      return I18N.t("Zu viele Versuche. Bitte warten Sie einen Moment.");
    }

    /**
     * ohne Statuscode kommt der Fehler nicht vom Server, sondern aus dem Browser:
     * navigator.credentials wirft eine DOMException, und deren Name benennt die
     * Ursache sehr genau. Sie zu verschlucken macht jeden Passkey-Fehler zu
     * derselben nichtssagenden Meldung - dabei sind die Ursachen voellig
     * verschieden, von "gibt es hier schon" bis "Domain passt nicht"
     */
    const name = error && error.name ? String(error.name) : '';

    if (name === 'InvalidStateError') {
      return I18N.t("Für dieses Konto ist auf diesem Gerät bereits ein Passkey hinterlegt.");
    }

    if (name === 'NotSupportedError' || name === 'ConstraintError') {
      return I18N.t("Dieser Browser oder dieses Gerät unterstützt die benötigte Art von Passkey nicht.");
    }

    if (name === 'SecurityError') {
      return I18N.t("Die Adresse dieser Seite passt nicht zur hinterlegten Passkey-Domain.");
    }

    if (name) {
      /**
       * jeder andere Browser-Fehler wird benannt statt verborgen - ohne den Namen
       * ist eine Ferndiagnose praktisch unmoeglich
       */
      return fallback + ' (' + name + ')';
    }

    return fallback;
  };

  /**
   * formatDate
   * @param value iso timestamp as the backend delivers it
   */
  const formatDate = (value: string | null): string => {
    if (!value) {
      return '—';
    }

    const parsed = moment(value);

    return parsed.isValid() ? parsed.format('DD.MM.YYYY HH:mm') : value;
  };

  /**
   * fill
   * I18N.t(text, args) crashes in this project (broken sprintf import), so every
   * placeholder is replaced here
   * @param text already translated
   * @param values
   */
  const fill = (text: string, values: { [key: string]: string }): string => {
    let result = text;

    /**
     * %s and not {name}: I18N.t() hands the catalogue entry back as a compiled
     * function, and that function treats {…} as an interpolation of its own -
     * with nothing to interpolate it simply drops it, which is how "Aktiv seit
     * {date}." reached the screen as "Aktiv seit .". %s survives untouched; the
     * existing catalogue key "%s Treffer von %s" relies on the same thing.
     * replaced in order, so the object keys have to be written in the order the
     * placeholders appear in the text
     */
    Object.keys(values).forEach((key) => {
      result = result.replace('%s', values[key]);
    });

    return result;
  };

  /**
   * suggestedPasskeyName
   */
  const suggestedPasskeyName = (): string => {
    const detected = detectDeviceName();

    if (!detected.browser || !detected.system) {
      return I18N.t("Neuer Passkey");
    }

    return fill(I18N.t("%s auf %s"), {
      browser: detected.browser,
      system: detected.system
    });
  };

  /**
   * handleSetupTotp
   * creates the secret. it is not armed yet - only the confirmation below is
   */
  const handleSetupTotp = async () => {
    setTotpMessage(null);
    setBusy('totp-setup');

    try {
      const setup = await serviceTwoFactor.setupTotp();

      setTotpSetup(setup);
      setTotpCode('');
    } catch (error) {
      setTotpMessage({
        kind: 'error',
        text: describeError(error, I18N.t("Die Einrichtung der Authenticator-App ist fehlgeschlagen."))
      });
    } finally {
      setBusy('');
    }
  };

  /**
   * handleConfirmTotp
   * @param event
   */
  const handleConfirmTotp = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTotpMessage(null);
    setBusy('totp-confirm');

    try {
      const result = await serviceTwoFactor.confirmTotp(totpCode.trim());

      setTotpSetup(null);
      setTotpCode('');
      setFreshCodes({ codes: result.recovery_codes, source: 'totp' });
      setTotpMessage({
        kind: 'success',
        text: I18N.t("Die Authenticator-App ist jetzt aktiv.")
      });
      reload();
    } catch (error) {
      setTotpMessage({
        kind: 'error',
        text: describeError(error, I18N.t("Der Code wurde nicht akzeptiert. Bitte versuchen Sie es mit dem aktuellen Code erneut."))
      });
    } finally {
      setBusy('');
    }
  };

  /**
   * handleDisableTotp
   * @param password
   */
  const handleDisableTotp = async (password: string) => {
    setTotpMessage(null);
    setBusy('totp-disable');

    try {
      await serviceTwoFactor.disableTotp(password);
      setConfirm({ kind: 'none' });
      setTotpMessage({
        kind: 'success',
        text: I18N.t("Die Authenticator-App wurde deaktiviert.")
      });
      reload();
    } catch (error) {
      setTotpMessage({
        kind: 'error',
        text: describeError(error, I18N.t("Die Authenticator-App konnte nicht deaktiviert werden."))
      });
    } finally {
      setBusy('');
    }
  };

  /**
   * handleAddPasskey
   *
   * three steps that belong together: options from the backend, the credential
   * from the browser, and the registration. an aborted browser dialog is not an
   * error the user needs to be alarmed about, so it is reported as a notice
   *
   * @param event
   */
  const handleAddPasskey = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPasskeyMessage(null);
    setBusy('passkey-add');

    const name = passkeyName.trim() || suggestedPasskeyName();

    try {
      const challenge = await serviceTwoFactor.passkeyRegistrationOptions(),
        credential = await createCredential(challenge.publicKey);

      await serviceTwoFactor.registerPasskey(challenge.challengeToken, credential, name);

      setPasskeyName('');
      setPasskeyMessage({
        kind: 'success',
        text: fill(I18N.t("Der Passkey „%s“ wurde hinzugefügt."), { name: name })
      });
      reload();
    } catch (error) {
      /** der volle Fehler in die Konsole: die Meldung auf der Seite muss kurz
          bleiben, die Ferndiagnose braucht aber Name UND Text der DOMException */
      console.error('Passkey-Registrierung fehlgeschlagen:', error);

      const aborted = error && ((error as any).name === 'NotAllowedError'
        || (error as any).name === 'AbortError');

      setPasskeyMessage(aborted
        /**
         * this dialog registers a passkey, it does not log anyone in - calling
         * the abort a cancelled login would describe a step the user is not in
         */
        ? { kind: 'info', text: I18N.t("Das Hinzufügen des Passkeys wurde abgebrochen.") }
        : {
          kind: 'error',
          text: describeError(error, I18N.t("Der Passkey konnte nicht hinzugefügt werden."))
        });
    } finally {
      setBusy('');
    }
  };

  /**
   * handleRenamePasskey
   * @param event
   */
  const handleRenamePasskey = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!renamingId) {
      return;
    }

    setPasskeyMessage(null);
    setBusy('passkey-rename');

    try {
      await serviceTwoFactor.renamePasskey(renamingId, renameValue.trim());
      setRenamingId(null);
      setRenameValue('');
      setPasskeyMessage({
        kind: 'success',
        text: I18N.t("Der Passkey wurde umbenannt.")
      });
      reload();
    } catch (error) {
      setPasskeyMessage({
        kind: 'error',
        text: describeError(error, I18N.t("Der Passkey konnte nicht umbenannt werden."))
      });
    } finally {
      setBusy('');
    }
  };

  /**
   * handleDeletePasskey
   * @param id
   * @param password
   */
  const handleDeletePasskey = async (id: string, password: string) => {
    setPasskeyMessage(null);
    setBusy('passkey-delete');

    try {
      await serviceTwoFactor.deletePasskey(id, password);
      setConfirm({ kind: 'none' });
      setPasskeyMessage({
        kind: 'success',
        text: I18N.t("Der Passkey wurde gelöscht.")
      });
      reload();
    } catch (error) {
      setPasskeyMessage({
        kind: 'error',
        text: describeError(error, I18N.t("Der Passkey konnte nicht gelöscht werden."))
      });
    } finally {
      setBusy('');
    }
  };

  /**
   * handleRegenerateCodes
   * @param password
   */
  const handleRegenerateCodes = async (password: string) => {
    setRecoveryMessage(null);
    setBusy('recovery-regenerate');

    try {
      const result = await serviceTwoFactor.regenerateRecoveryCodes(password);

      setConfirm({ kind: 'none' });
      setFreshCodes({ codes: result.recovery_codes, source: 'regenerate' });
      setRecoveryMessage({
        kind: 'success',
        text: I18N.t("Es wurden neue Wiederherstellungscodes erzeugt. Die alten Codes gelten nicht mehr.")
      });
      reload();
    } catch (error) {
      setRecoveryMessage({
        kind: 'error',
        text: describeError(error, I18N.t("Die Wiederherstellungscodes konnten nicht neu erzeugt werden."))
      });
    } finally {
      setBusy('');
    }
  };

  /**
   * handleForgetDevices
   */
  const handleForgetDevices = async () => {
    setDeviceMessage(null);
    setBusy('devices-forget');

    try {
      await serviceTwoFactor.forgetTrustedDevices();
      setConfirm({ kind: 'none' });
      setDeviceMessage({
        kind: 'success',
        text: I18N.t("Alle vertrauten Geräte wurden entfernt. Beim nächsten Anmelden wird überall wieder nach dem zweiten Faktor gefragt.")
      });
      reload();
    } catch (error) {
      setDeviceMessage({
        kind: 'error',
        text: describeError(error, I18N.t("Die vertrauten Geräte konnten nicht entfernt werden."))
      });
    } finally {
      setBusy('');
    }
  };

  /**
   * the live region has to exist before it ever has text, otherwise the first
   * announcement is swallowed (same reason as in DataTable)
   */
  const loadingRegion = (
    <p data-two-factor-loading aria-live="polite">
      { refreshing ? I18N.t("Daten werden geladen …") : "" }
    </p>
  );

  if (!status) {
    return (
      <div data-two-factor-settings>
        { loadingRegion }
        { loadFailed && (
          <InlineMessage
            section="two-factor-load"
            message={ {
              kind: 'error',
              text: I18N.t("Die Zwei-Faktor-Einstellungen konnten nicht geladen werden.")
            } }
            onRetry={ reload }
          />
        ) }
      </div>
    );
  }

  return (
    <div data-two-factor-settings>
      { loadingRegion }

      { loadFailed && (
        <InlineMessage
          section="two-factor-load"
          message={ {
            kind: 'error',
            text: I18N.t("Die Zwei-Faktor-Einstellungen konnten nicht aktualisiert werden.")
          } }
          onRetry={ reload }
        />
      ) }

      { /* ---------------------------------------------------------------
           authenticator app
           --------------------------------------------------------------- */ }
      <div data-two-factor-section="totp" aria-labelledby="two-factor-totp-heading">
        <h3 id="two-factor-totp-heading">{ I18N.t("Authenticator-App") }</h3>

        <p data-two-factor-status>
          { status.totp.enabled
            ? fill(
              I18N.t("Aktiv seit %s."),
              { date: formatDate(status.totp.confirmed_at) }
            )
            : I18N.t("Nicht eingerichtet.") }
        </p>

        <InlineMessage section="totp" message={ totpMessage } />

        { !status.totp.enabled && !totpSetup && (
          <button
            type="button"
            data-primary-dark
            data-two-factor-action="totp-setup"
            disabled={ busy === 'totp-setup' }
            onClick={ handleSetupTotp }
          >
            { I18N.t("Authenticator-App einrichten") }
          </button>
        ) }

        { !status.totp.enabled && totpSetup && (
          <div data-totp-setup>
            <p data-totp-hint>
              { I18N.t("Scannen Sie den QR-Code mit Ihrer Authenticator-App oder tragen Sie den Schlüssel von Hand ein. Geben Sie danach den angezeigten sechsstelligen Code ein.") }
            </p>

            { totpSetup.otpauth_uri ? (
              <div data-totp-qr>
                { /* the svg variant scales without blurring and needs no canvas
                     permission; the title is what a screen reader announces, the
                     secret below is the accessible way to enter the same data */ }
                <QRCodeSVG
                  value={ totpSetup.otpauth_uri }
                  size={ 200 }
                  level="M"
                  title={ I18N.t("QR-Code zur Einrichtung der Authenticator-App") }
                />
              </div>
            ) : (
              <p data-totp-qr-missing>
                { I18N.t("Der QR-Code konnte nicht erzeugt werden. Bitte tragen Sie den Schlüssel von Hand ein.") }
              </p>
            ) }

            <div data-totp-secret>
              <span data-totp-secret-label id="two-factor-totp-secret-label">
                { I18N.t("Schlüssel zur manuellen Eingabe") }
              </span>
              { /* readOnly input instead of plain text: it can be focused,
                   selected and copied with the keyboard alone */ }
              <input
                type="text"
                readOnly
                data-totp-secret-value
                id="two-factor-totp-secret"
                name="two-factor-totp-secret"
                aria-labelledby="two-factor-totp-secret-label"
                value={ totpSetup.secret }
                onFocus={ (event) => event.target.select() }
              />
            </div>

            <dia-form>
              <form onSubmit={ handleConfirmTotp }>
                <dia-form-area>
                  <dia-form-line>
                    <dia-form-control>
                      <input
                        type="text"
                        id="two-factor-totp-code"
                        name="two-factor-totp-code"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        placeholder={ I18N.t("6-stelliger Code") }
                        value={ totpCode }
                        onChange={ (event) => setTotpCode(event.target.value) }
                        required
                      />
                      <label htmlFor="two-factor-totp-code">
                        { I18N.t("Code aus der Authenticator-App") }
                      </label>
                    </dia-form-control>
                    <dia-form-control>
                      <button type="submit" data-primary-dark disabled={ busy === 'totp-confirm' }>
                        { I18N.t("Bestätigen") }
                      </button>
                    </dia-form-control>
                    <dia-form-control>
                      <button
                        type="button"
                        data-secondary
                        onClick={ () => {
                          setTotpSetup(null);
                          setTotpCode('');
                        } }
                      >
                        { I18N.t("Abbrechen") }
                      </button>
                    </dia-form-control>
                  </dia-form-line>
                </dia-form-area>
              </form>
            </dia-form>
          </div>
        ) }

        { status.totp.enabled && (
          <>
            <button
              type="button"
              data-secondary
              data-two-factor-action="totp-disable"
              onClick={ () => setConfirm({ kind: 'disable-totp' }) }
            >
              { I18N.t("Authenticator-App deaktivieren") }
            </button>
            <PasswordConfirm
              open={ confirm.kind === 'disable-totp' }
              id="two-factor-totp-disable-password"
              question={ I18N.t("Die Authenticator-App wirklich deaktivieren? Ihr Konto ist danach nur noch durch das Passwort geschützt.") }
              confirmLabel={ I18N.t("Deaktivieren") }
              busy={ busy === 'totp-disable' }
              onConfirm={ handleDisableTotp }
              onCancel={ () => setConfirm({ kind: 'none' }) }
            />
          </>
        ) }

        { freshCodes && freshCodes.source === 'totp' && (
          <RecoveryCodesDisplay
            codes={ freshCodes.codes }
            section="totp"
            onDismiss={ () => setFreshCodes(null) }
          />
        ) }
      </div>

      { /* ---------------------------------------------------------------
           passkeys
           --------------------------------------------------------------- */ }
      <div data-two-factor-section="passkeys" aria-labelledby="two-factor-passkeys-heading">
        <h3 id="two-factor-passkeys-heading">{ I18N.t("Passkeys") }</h3>

        { /* not hidden when unsupported: a missing section reads as "this
             account cannot do passkeys", which is the wrong conclusion */ }
        { !webAuthnAvailable && (
          <p data-two-factor-unsupported role="note">
            { I18N.t("Dieser Browser unterstützt keine Passkeys. Passkeys benötigen eine gesicherte Verbindung (HTTPS) und einen aktuellen Browser. Ihre bereits registrierten Passkeys bleiben erhalten und lassen sich hier weiterhin umbenennen oder löschen.") }
          </p>
        ) }

        <InlineMessage section="passkeys" message={ passkeyMessage } />

        { status.passkeys.length === 0 ? (
          <p data-two-factor-empty>{ I18N.t("Es ist noch kein Passkey registriert.") }</p>
        ) : (
          <ul data-passkey-list>
            { status.passkeys.map((passkey) => (
              <li key={ passkey.id } data-passkey-item>
                <span data-passkey-name>{ passkey.name || I18N.t("Passkey") }</span>
                <span data-passkey-created>
                  { fill(I18N.t("Angelegt am %s"), { date: formatDate(passkey.created_at) }) }
                </span>
                <span data-passkey-last-used>
                  { passkey.last_used_at
                    ? fill(I18N.t("Zuletzt verwendet am %s"), { date: formatDate(passkey.last_used_at) })
                    : I18N.t("Noch nicht verwendet") }
                </span>

                <span data-passkey-actions>
                  <button
                    type="button"
                    data-secondary
                    data-small
                    onClick={ () => {
                      setRenamingId(passkey.id);
                      setRenameValue(passkey.name);
                    } }
                  >
                    { I18N.t("Umbenennen") }
                  </button>
                  <button
                    type="button"
                    data-secondary
                    data-small
                    onClick={ () => setConfirm({ kind: 'delete-passkey', id: passkey.id }) }
                  >
                    { I18N.t("Löschen") }
                  </button>
                </span>

                { renamingId === passkey.id && (
                  <dia-form>
                    <form onSubmit={ handleRenamePasskey }>
                      <dia-form-area>
                        <dia-form-line>
                          <dia-form-control>
                            <input
                              type="text"
                              id={ 'passkey-rename-' + passkey.id }
                              name={ 'passkey-rename-' + passkey.id }
                              maxLength={ 100 }
                              placeholder={ I18N.t("Name des Passkeys") }
                              value={ renameValue }
                              onChange={ (event) => setRenameValue(event.target.value) }
                              required
                            />
                            <label htmlFor={ 'passkey-rename-' + passkey.id }>
                              { I18N.t("Neuer Name") }
                            </label>
                          </dia-form-control>
                          <dia-form-control>
                            <button type="submit" data-primary-dark data-small disabled={ busy === 'passkey-rename' }>
                              { I18N.t("Speichern") }
                            </button>
                          </dia-form-control>
                          <dia-form-control>
                            <button
                              type="button"
                              data-secondary
                              data-small
                              onClick={ () => {
                                setRenamingId(null);
                                setRenameValue('');
                              } }
                            >
                              { I18N.t("Abbrechen") }
                            </button>
                          </dia-form-control>
                        </dia-form-line>
                      </dia-form-area>
                    </form>
                  </dia-form>
                ) }

                <PasswordConfirm
                  open={ confirm.kind === 'delete-passkey' && confirm.id === passkey.id }
                  id={ 'passkey-delete-password-' + passkey.id }
                  question={ fill(
                    I18N.t("Den Passkey „%s“ wirklich löschen? Er kann danach nicht mehr zum Anmelden verwendet werden."),
                    { name: passkey.name || I18N.t("Passkey") }
                  ) }
                  confirmLabel={ I18N.t("Löschen") }
                  busy={ busy === 'passkey-delete' }
                  onConfirm={ (password) => handleDeletePasskey(passkey.id, password) }
                  onCancel={ () => setConfirm({ kind: 'none' }) }
                />
              </li>
            )) }
          </ul>
        ) }

        { webAuthnAvailable && (
          <dia-form>
            <form onSubmit={ handleAddPasskey }>
              <dia-form-area>
                <dia-form-line>
                  <dia-form-control>
                    <input
                      type="text"
                      id="passkey-new-name"
                      name="passkey-new-name"
                      maxLength={ 100 }
                      placeholder={ suggestedPasskeyName() }
                      value={ passkeyName }
                      onChange={ (event) => setPasskeyName(event.target.value) }
                      onFocus={ () => {
                        /**
                         * the suggestion is only written into the field once the
                         * user actually goes for it - a prefilled input that was
                         * never touched looks like saved data
                         */
                        if (!passkeyName) {
                          setPasskeyName(suggestedPasskeyName());
                        }
                      } }
                    />
                    <label htmlFor="passkey-new-name">{ I18N.t("Name des neuen Passkeys") }</label>
                  </dia-form-control>
                  <dia-form-control>
                    <button
                      type="submit"
                      data-primary-dark
                      data-two-factor-action="passkey-add"
                      disabled={ busy === 'passkey-add' }
                    >
                      { I18N.t("Passkey hinzufügen") }
                    </button>
                  </dia-form-control>
                </dia-form-line>
              </dia-form-area>
            </form>
          </dia-form>
        ) }
      </div>

      { /* ---------------------------------------------------------------
           recovery codes
           --------------------------------------------------------------- */ }
      <div data-two-factor-section="recovery" aria-labelledby="two-factor-recovery-heading">
        <h3 id="two-factor-recovery-heading">{ I18N.t("Wiederherstellungscodes") }</h3>

        <p data-two-factor-status>
          { fill(
            I18N.t("Noch %s von %s Codes verfügbar."),
            {
              remaining: String(status.recovery_codes.remaining),
              total: String(status.recovery_codes.total)
            }
          ) }
        </p>

        <InlineMessage section="recovery" message={ recoveryMessage } />

        <button
          type="button"
          data-secondary
          data-two-factor-action="recovery-regenerate"
          onClick={ () => setConfirm({ kind: 'regenerate-codes' }) }
        >
          { I18N.t("Neue Codes erzeugen") }
        </button>

        <PasswordConfirm
          open={ confirm.kind === 'regenerate-codes' }
          id="recovery-regenerate-password"
          question={ I18N.t("Wirklich neue Wiederherstellungscodes erzeugen? Alle bisherigen Codes verlieren damit ihre Gültigkeit.") }
          confirmLabel={ I18N.t("Neue Codes erzeugen") }
          busy={ busy === 'recovery-regenerate' }
          onConfirm={ handleRegenerateCodes }
          onCancel={ () => setConfirm({ kind: 'none' }) }
        />

        { freshCodes && freshCodes.source === 'regenerate' && (
          <RecoveryCodesDisplay
            codes={ freshCodes.codes }
            section="regenerate"
            onDismiss={ () => setFreshCodes(null) }
          />
        ) }
      </div>

      { /* ---------------------------------------------------------------
           trusted devices
           --------------------------------------------------------------- */ }
      <div data-two-factor-section="devices" aria-labelledby="two-factor-devices-heading">
        <h3 id="two-factor-devices-heading">{ I18N.t("Vertraute Geräte") }</h3>

        <InlineMessage section="devices" message={ deviceMessage } />

        { status.trusted_devices.length === 0 ? (
          <p data-two-factor-empty>{ I18N.t("Es ist kein Gerät als vertraut markiert.") }</p>
        ) : (
          <ul data-device-list>
            { status.trusted_devices.map((device) => (
              <li key={ device.id } data-device-item>
                <span data-device-label>{ device.label || I18N.t("Unbenanntes Gerät") }</span>
                <span data-device-last-used>
                  { device.last_used_at
                    ? fill(I18N.t("Zuletzt verwendet am %s"), { date: formatDate(device.last_used_at) })
                    : I18N.t("Noch nicht verwendet") }
                </span>
                <span data-device-expires>
                  { fill(I18N.t("Gültig bis %s"), { date: formatDate(device.expires_at) }) }
                </span>
              </li>
            )) }
          </ul>
        ) }

        <button
          type="button"
          data-secondary
          data-two-factor-action="devices-forget"
          disabled={ status.trusted_devices.length === 0 }
          onClick={ () => setConfirm({ kind: 'forget-devices' }) }
        >
          { I18N.t("Alle Geräte vergessen") }
        </button>

        { /* no password here: the endpoint does not ask for one, and inventing a
             check the backend does not make would only pretend safety */ }
        <PasswordConfirm
          open={ confirm.kind === 'forget-devices' }
          id="devices-forget-confirm"
          question={ I18N.t("Wirklich alle vertrauten Geräte vergessen? Beim nächsten Anmelden wird überall wieder nach dem zweiten Faktor gefragt.") }
          confirmLabel={ I18N.t("Alle Geräte vergessen") }
          requirePassword={ false }
          busy={ busy === 'devices-forget' }
          onConfirm={ handleForgetDevices }
          onCancel={ () => setConfirm({ kind: 'none' }) }
        />
      </div>
    </div>
  );
};

export default TwoFactorSettings;
